import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import './DataPengiriman.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:5000';

function DataPengiriman({ user, onLogout }) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editId, setEditId] = useState(null);
  const [pengirimanList, setPengirimanList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [kurirList, setKurirList] = useState([]);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isAuthenticatedRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 5;
  
  const [formData, setFormData] = useState({
    no_resi: '',
    nama_pengirim: '',
    no_telp_pengirim: '',
    alamat_asal: '',
    nama_penerima: '',
    no_telp_penerima: '',
    alamat_tujuan: '',
    nama_barang: '',
    jenis_barang: '',
    jumlah_barang: 1,
    berat: '',
    biaya_pengiriman: '',
    status: 'dalamproses',
    tanggal: new Date().toISOString().slice(0, 16),
    no_pol_armada: '-',
    nama_kurir: '-'
  });

  const [detailBarang, setDetailBarang] = useState([
    { nama_barang: '', jenis_barang: '', berat: '' }
  ]);


const formatRupiah = (angka) => {
  const numberString = angka.toString().replace(/[^,\d]/g, '');
  const split = numberString.split(',');
  const sisa = split[0].length % 3;
  let rupiah = split[0].substr(0, sisa);
  const ribuan = split[0].substr(sisa).match(/\d{3}/gi);
  
  if (ribuan) {
    const separator = sisa ? '.' : '';
    rupiah += separator + ribuan.join('.');
  }
  
  rupiah = split[1] !== undefined ? rupiah + ',' + split[1] : rupiah;
  return rupiah;
};

const handleBiayaChange = (e) => {
  const value = e.target.value.replace(/\D/g, ''); // Hapus semua karakter non-digit
  setFormData(prev => ({
    ...prev,
    biaya_pengiriman: value
  }));
};

  const fetchPengiriman = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await axios.get(`${API_URL}/api/pengiriman?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPengirimanList(response.data.data);
      setError('');
    } catch (err) {
      setError('Gagal memuat data pengiriman');
      console.error('Error fetching pengiriman:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStatus]);

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found, skipping WebSocket connection');
      return;
    }

    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
        console.log('WebSocket already connected or connecting');
        return;
      }
    }

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.log('Max reconnection attempts reached');
      return;
    }

    try {
     
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
       
        reconnectAttemptsRef.current = 0;
        isAuthenticatedRef.current = false;
        ws.send(JSON.stringify({ type: 'authenticate', token }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'authenticated') {
           
            isAuthenticatedRef.current = true;
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log(`WebSocket closed with code: ${event.code}`);
        wsRef.current = null;
        isAuthenticatedRef.current = false;
        
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        const shouldReconnect = 
          event.code !== 1000 && 
          localStorage.getItem('token') &&
          reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS;
        
        if (shouldReconnect) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectWebSocket();
          }, delay);
        }
      };
      wsRef.current = ws;
    } catch (err) {
      console.error('WebSocket init error:', err);
      wsRef.current = null;
    }
  }, []);

  const fetchKurirList = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/api/users?role=kurir`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    setKurirList(response.data.data || []);
  } catch (err) {
    console.error('Error fetching kurir list:', err);
  }
}, []);

// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  fetchPengiriman();
  fetchKurirList();
  
  const wsTimeout = setTimeout(() => {
    connectWebSocket();
  }, 500);

  return () => {
    clearTimeout(wsTimeout);
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      const ws = wsRef.current;
      ws.onclose = null;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, 'Component unmounted');
      }
      wsRef.current = null;
    }
    
    reconnectAttemptsRef.current = 0;
    isAuthenticatedRef.current = false;
  };
}, [fetchPengiriman, fetchKurirList, connectWebSocket]);



const generateNoResi = () => {
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  
  const noResi = `ZT${year}${month}${day}${random}`;
  
  setFormData(prev => ({
    ...prev,
    no_resi: noResi
  }));
  
  setSuccess(`No Resi berhasil digenerate: ${noResi}`);
  setTimeout(() => setSuccess(''), 3000);
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'jumlah_barang') {
      const jumlah = parseInt(value) || 1;
      setFormData(prev => ({ ...prev, [name]: value }));
      
      if (jumlah > 1) {
        setDetailBarang(prev => {
          const newDetail = Array.from({ length: jumlah }, (_, i) => 
            prev[i] || { nama_barang: '', jenis_barang: '', berat: '' }
          );
          return newDetail;
        });
      } else {
        setDetailBarang([{ nama_barang: '', jenis_barang: '', berat: '' }]);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDetailBarangChange = (index, field, value) => {
    const newDetail = [...detailBarang];
    newDetail[index][field] = value;
    setDetailBarang(newDetail);
  };

  const resetForm = () => {
    setFormData({
      no_resi: '',
      nama_pengirim: '',
      no_telp_pengirim: '',
      alamat_asal: '',
      nama_penerima: '',
      no_telp_penerima: '',
      alamat_tujuan: '',
      nama_barang: '',
      jenis_barang: '',
      jumlah_barang: 1,
      berat: '',
      biaya_pengiriman: '',
      status: 'dalamproses',
      tanggal: new Date().toISOString().slice(0, 16),
      no_pol_armada: '-',
      nama_kurir: '-'
    });
    setDetailBarang([{ nama_barang: '', jenis_barang: '', berat: '' }]);
    setModalMode('add');
    setEditId(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setError('');
    setSuccess('');
  };

  const handleOpenAddModal = () => {
    resetForm();
    setModalMode('add');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      let dataToSend = { ...formData };
      
      if (parseInt(formData.jumlah_barang) > 1) {
        dataToSend.nama_barang = detailBarang
          .map((b, i) => `${i + 1}. ${b.nama_barang}`)
          .join(' | ');
        
        const jenisBarangList = detailBarang
          .map(b => b.jenis_barang)
          .filter(j => j && j.trim() !== '');
        dataToSend.jenis_barang = jenisBarangList.length > 0 
          ? jenisBarangList.join(', ') 
          : '';
        
        const totalBerat = detailBarang.reduce((sum, b) => {
          const berat = parseFloat(b.berat) || 0;
          return sum + berat;
        }, 0);
        dataToSend.berat = totalBerat.toString();
      }
      
      if (modalMode === 'edit') {
        await axios.put(`${API_URL}/api/pengiriman/${editId}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Data pengiriman berhasil diupdate!');
      } else {
        await axios.post(`${API_URL}/api/pengiriman`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        try {
          const statusData = {
            no_resi: dataToSend.no_resi,
            status: dataToSend.status,
            lokasi: dataToSend.alamat_asal || 'Gudang',
            tanggal: new Date().toISOString().slice(0, 16)
          };
          
          await axios.post(`${API_URL}/api/status-pengiriman`, statusData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          setSuccess('Data pengiriman dan status awal berhasil ditambahkan!');
        } catch (statusErr) {
          console.error('Error adding initial status:', statusErr);
          setSuccess('Data pengiriman berhasil ditambahkan, tapi gagal menambahkan status awal');
        }
      }
      
      handleCloseModal();
      fetchPengiriman();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menyimpan data');
      console.error('Error saving pengiriman:', err);
    } finally {
      setLoading(false);
    }
  };

// Ganti fungsi handleEdit dengan kode ini:

const handleEdit = async (id) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/api/pengiriman/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const data = response.data.data;
    setFormData({
      no_resi: data.no_resi,
      nama_pengirim: data.nama_pengirim,
      no_telp_pengirim: data.no_telp_pengirim || '',
      alamat_asal: data.alamat_asal || '',
      nama_penerima: data.nama_penerima,
      no_telp_penerima: data.no_telp_penerima || '',
      alamat_tujuan: data.alamat_tujuan,
      nama_barang: data.nama_barang,
      jenis_barang: data.jenis_barang || '',
      jumlah_barang: data.jumlah_barang || 1,
      berat: data.berat,
      biaya_pengiriman: data.biaya_pengiriman,
      status: data.status,
      tanggal: new Date(data.tanggal).toISOString().slice(0, 16),
      no_pol_armada: data.no_pol_armada || '-',
      nama_kurir: data.nama_kurir || '-'
    });
    
    // Parse detail barang dari data yang tersimpan
    if (data.nama_barang.includes('|')) {
      // Multiple items
      const namaBarangArr = data.nama_barang.split('|').map(s => s.trim());
      const jenisBarangArr = data.jenis_barang ? data.jenis_barang.split(',').map(s => s.trim()) : [];
      const jumlahBarang = data.jumlah_barang || namaBarangArr.length;
      const beratPerItem = (parseFloat(data.berat) / jumlahBarang).toFixed(2);
      
      const parsedDetail = namaBarangArr.map((nama, idx) => ({
        nama_barang: nama.replace(/^\d+\.\s*/, ''), // Hapus nomor jika ada
        jenis_barang: jenisBarangArr[idx] || '',
        berat: beratPerItem
      }));
      
      setDetailBarang(parsedDetail);
    } else {
      // Single item
      setDetailBarang([{
        nama_barang: data.nama_barang,
        jenis_barang: data.jenis_barang || '',
        berat: data.berat
      }]);
    }
    
    setEditId(id);
    setModalMode('edit');
    setShowModal(true);
  } catch (err) {
    setError('Gagal memuat data untuk edit');
    console.error('Error fetching detail:', err);
  }
};

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus data ini?')) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/pengiriman/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Data berhasil dihapus!');
      fetchPengiriman();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menghapus data');
      console.error('Error deleting:', err);
    } finally {
      setLoading(false);
    }
  };

const handlePrintSuratJalan = (item) => {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  let detailBarangHtml = '';
  if (item.nama_barang.includes('|')) {
    const namaBarangArr = item.nama_barang.split('|').map(s => s.trim());
    const jenisBarangArr = item.jenis_barang ? item.jenis_barang.split(',').map(s => s.trim()) : [];
    
    namaBarangArr.forEach((nama, idx) => {
      const jenis = jenisBarangArr[idx] || '';
      detailBarangHtml += `
        <tr>
          <td style="border: 1px solid #000; padding: 8px; text-align: center;">${idx + 1}</td>
          <td style="border: 1px solid #000; padding: 8px;">${nama.replace(/^\d+\.\s*/, '')}</td>
          <td style="border: 1px solid #000; padding: 8px;">${jenis}</td>
          <td style="border: 1px solid #000; padding: 8px;"></td>
        </tr>
      `;
    });
  } else {
    detailBarangHtml = `
      <tr>
        <td style="border: 1px solid #000; padding: 8px; text-align: center;">1</td>
        <td style="border: 1px solid #000; padding: 8px;">${item.nama_barang}</td>
        <td style="border: 1px solid #000; padding: 8px;">${item.jenis_barang || ''}</td>
        <td style="border: 1px solid #000; padding: 8px;">${item.berat} Kg</td>
      </tr>
    `;
  }
  
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Surat Jalan - ${item.no_resi}</title>
      <style>
        @media print {
          @page { 
            margin: 0.5cm;
            size: A4;
          }
          body { margin: 0; }
          .page-break { page-break-after: always; }
          .no-print { display: none; }
        }
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        
        body {
          font-family: Arial, sans-serif;
          font-size: 11pt;
          line-height: 1.4;
        }
        
        .surat-jalan-container {
          padding: 20px;
          max-width: 210mm;
          margin: 0 auto;
        }
        
        .copy-label {
          text-align: right;
          font-size: 10pt;
          font-weight: bold;
          color: #0066cc;
          margin-bottom: 10px;
          padding: 8px 15px;
          background: #e3f2fd;
          border-left: 4px solid #0066cc;
        }
        
        .header {
          border: 2px solid #000;
          padding: 15px;
          margin-bottom: 20px;
        }
        
        .header h1 {
          font-size: 20pt;
          font-weight: bold;
          margin-bottom: 5px;
          text-align: center;
        }
        
        .header .subtitle {
          font-size: 10pt;
          text-align: center;
          margin-bottom: 15px;
        }
        
        .info-row {
          margin-bottom: 8px;
          display: flex;
        }
        
        .info-label {
          width: 120px;
          font-weight: bold;
        }
        
        .info-value {
          flex: 1;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        
        table th {
          border: 1px solid #000;
          padding: 10px;
          background-color: #f0f0f0;
          font-weight: bold;
          text-align: center;
        }
        
        table td {
          border: 1px solid #000;
          padding: 8px;
        }
        
        .note {
          margin-top: 20px;
          padding: 10px;
          border: 1px solid #ccc;
          background-color: #f9f9f9;
          font-size: 9pt;
        }
        
        .download-button {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          padding: 15px 30px;
          background: #0066cc;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14pt;
          font-weight: bold;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(0,102,204,0.3);
          z-index: 9999;
        }
        
        .download-button:hover {
          background: #0052a3;
        }
      </style>
      <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
    </head>
    <body>
      <button class="download-button no-print" onclick="downloadPDF()">
        📥 Download PDF
      </button>

      <!-- SALINAN 1: UNTUK PENGIRIM -->
      <div class="surat-jalan-container">
        <div class="copy-label"> SALINAN UNTUK PENGIRIM</div>
        
        <div class="header">
          <h1>ZHAFRAN TRANSPORT</h1>
          <div class="subtitle">
            Jl. Wonoplintahan, Prambon
          </div>
          
          <div style="border-top: 2px solid #000; padding-top: 15px; margin-top: 15px;">
            <div style="display: flex; justify-content: space-between;">
              <div style="width: 48%;">
                <div class="info-row">
                  <span class="info-label">No</span>
                  <span class="info-value">: ${item.no_resi}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Tanggal</span>
                  <span class="info-value">: ${new Date(item.tanggal).toLocaleDateString('id-ID', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}</span>
                </div>
                                ${item.no_pol_armada !== '-' ? `
                <div class="info-row">
                  <span class="info-label">No Pol</span>
                  <span class="info-value">: ${item.no_pol_armada}</span>
                </div>
                ` : ''}
              </div>
              <div style="width: 48%;">
                <div class="info-row">
                  <span class="info-label">Tujuan</span>
                </div>
                <div class="info-row">
                  <span class="info-value" style="font-weight: bold;">${item.nama_penerima}</span>
                </div>
                <div class="info-row">
                  <span class="info-value">${item.alamat_tujuan}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style="margin-bottom: 15px; font-weight: bold;">
          Harap diterima dengan baik barang-barang tersebut dibawah ini:
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 40px;">No</th>
              <th>Macam / Jenis Barang</th>
              <th style="width: 150px;">Jumlah Barang</th>
              <th style="width: 120px;">Keterangan</th>
            </tr>
          </thead>
          <tbody>
            ${detailBarangHtml}
          </tbody>
        </table>

        <div class="note">
          <strong>Catatan:</strong>
          <ul style="margin-left: 20px; margin-top: 5px;">
            <li>Surat jalan ini adalah bukti sah pengiriman barang</li>
            <li>Mohon periksa kondisi barang saat menerima</li>
            <li>Hubungi kami jika ada kerusakan atau kekurangan</li>
          </ul>
        </div>
      </div>

      <!-- PAGE BREAK -->
      <div class="page-break"></div>

      <!-- SALINAN 2: UNTUK KURIR -->
      <div class="surat-jalan-container">
        <div class="copy-label"> SALINAN UNTUK KURIR</div>
        
        <div class="header">
          <h1>ZHAFRAN TRANSPORT</h1>
          <div class="subtitle">
            Jl. Wonoplintahan, Prambon
          </div>
          
          <div style="border-top: 2px solid #000; padding-top: 15px; margin-top: 15px;">
            <div style="display: flex; justify-content: space-between;">
              <div style="width: 48%;">
                <div class="info-row">
                  <span class="info-label">No</span>
                  <span class="info-value">: ${item.no_resi}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Tanggal</span>
                  <span class="info-value">: ${new Date(item.tanggal).toLocaleDateString('id-ID', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}</span>
                </div>
                ${item.nama_kurir !== '-' ? `
                <div class="info-row">
                  <span class="info-label">Kurir</span>
                  <span class="info-value">: ${item.nama_kurir}</span>
                </div>
                ` : ''}
                ${item.no_pol_armada !== '-' ? `
                <div class="info-row">
                  <span class="info-label">No Pol</span>
                  <span class="info-value">: ${item.no_pol_armada}</span>
                </div>
                ` : ''}
              </div>
              <div style="width: 48%;">
                <div class="info-row">
                  <span class="info-label">Yth</span>
                </div>
                <div class="info-row">
                  <span class="info-value" style="font-weight: bold;">${item.nama_penerima}</span>
                </div>
                <div class="info-row">
                  <span class="info-value">${item.alamat_tujuan}</span>
                </div>
                ${item.no_telp_penerima ? `
                <div class="info-row">
                  <span class="info-label">Telp</span>
                  <span class="info-value">: ${item.no_telp_penerima}</span>
                </div>
                ` : ''}
              </div>
            </div>
          </div>
        </div>

        <div style="margin-bottom: 15px; font-weight: bold;">
          Harap diterima dengan baik barang-barang tersebut dibawah ini:
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 40px;">No</th>
              <th>Macam / Jenis Barang</th>
              <th style="width: 150px;">Jumlah Barang</th>
              <th style="width: 120px;">Keterangan</th>
            </tr>
          </thead>
          <tbody>
            ${detailBarangHtml}
          </tbody>
        </table>

        <div style="margin-top: 30px;">
          <table style="border: none;">
            <tr>
              <td style="border: none; width: 33%; vertical-align: top;">
                <strong>Penerima:</strong>
              </td>
              <td style="border: none; width: 33%; vertical-align: top;">
                <strong>Pengirim:</strong>
                <div style="margin-top: 5px;">${item.alamat_asal || item.nama_pengirim}</div>
              </td>
              <td style="border: none; width: 33%; vertical-align: top;">
                <strong>Mengetahui</strong>
              </td>
            </tr>
            <tr>
              <td style="border: none; padding-top: 80px;">
                <div style="border-top: 1px solid #000; padding-top: 5px; text-align: center;">
                  ( ........................... )
                </div>
              </td>
              <td style="border: none; padding-top: 80px;">
                <div style="border-top: 1px solid #000; padding-top: 5px; text-align: center;">
                  ( ........................... )
                </div>
              </td>
              <td style="border: none; padding-top: 80px;">
                <div style="border-top: 1px solid #000; padding-top: 5px; text-align: center;">
                  ( ........................... )
                </div>
              </td>
            </tr>
          </table>
        </div>

        <div class="note">
          <strong>Catatan untuk Kurir:</strong>
          <ul style="margin-left: 20px; margin-top: 5px;">
            <li>Pastikan barang sesuai dengan daftar sebelum berangkat</li>
            <li>Hubungi penerima sebelum tiba di lokasi tujuan</li>
            <li>Minta tanda tangan penerima setelah serah terima barang</li>
            <li>Segera laporkan jika ada kendala selama perjalanan</li>
          </ul>
        </div>
      </div>

      <script>
        function downloadPDF() {
          const element = document.body;
          const opt = {
            margin: [10, 10, 10, 10],
            filename: 'Surat-Jalan-${item.no_resi}.pdf',
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
          };
          
          // Hide download button before generating PDF
          document.querySelector('.download-button').style.display = 'none';
          
          html2pdf().set(opt).from(element).save().then(() => {
            // Show button again after download
            document.querySelector('.download-button').style.display = 'block';
            // Optionally close the window after download
            setTimeout(() => {
              window.close();
            }, 1000);
          });
        }
        
        // Auto-trigger download after page loads
        window.onload = function() {
          setTimeout(() => {
            downloadPDF();
          }, 500);
        }
      </script>
    </body>
    </html>
  `;
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
};

  const getStatusBadge = (status) => {
    const statusConfig = {
      dalamproses: { label: 'Dalam Proses', class: 'status-dalam-proses' },
      dikirim: { label: 'Dikirim', class: 'status-dikirim' },
      diterima: { label: 'Diterima', class: 'status-diterima' }
    };
    const config = statusConfig[status] || statusConfig.dalamproses;
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const handleNavigate = (page) => {
    switch (page) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'pengiriman':
        navigate('/pengiriman');
        break;
      case 'status-pengiriman':
        navigate('/status-pengiriman');
        break;
      case 'kelolaakun':
        navigate('/kelolaakun');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <>
      <Header user={user} onMenuToggle={handleToggleSidebar} />
      <div className="dashboard-layout" style={{ marginTop: '60px' }}>
        <Sidebar 
          user={user} 
          onLogout={onLogout}
          currentPage="pengiriman"
          onNavigate={handleNavigate}
          isOpen={sidebarOpen}
          onToggle={handleToggleSidebar}
        />
        <div className="dashboard-main">
          <div className="dashboard-container">
            <div className="data-pengiriman-page">
              <div className="page-header">
                <h1>
                  <i className="bi bi-box-seam me-2"></i>
                  Data Pengiriman
                </h1>
                <p>Kelola semua data pengiriman</p>
              </div>

              {success && (
                <div className="success-message">
                  <i className="bi bi-check-circle me-2"></i>
                  {success}
                </div>
              )}
              {error && (
                <div className="error-message">
                  <i className="bi bi-x-circle me-2"></i>
                  {error}
                </div>
              )}

              {user.role === 'admin' && (
                <div className="action-bar">
                  <button 
                    className="btn-primary"
                    onClick={handleOpenAddModal}
                    disabled={loading}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Tambah Pengiriman
                  </button>
                </div>
              )}

              <div className="pengiriman-list">
                <div className="list-header">
                  <h2>Daftar Pengiriman</h2>
                  <div className="list-filters">
                    <select 
                      className="filter-status"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="">Semua Status</option>
                      <option value="dalamproses">Dalam Proses</option>
                      <option value="dikirim">Dikirim</option>
                      <option value="diterima">Diterima</option>
                    </select>
                    <div className="search-wrapper">
                      <i className="bi bi-search search-icon"></i>
                      <input 
                        type="text" 
                        className="search-input"
                        placeholder="Cari no resi" 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {loading && (
                  <div className="loading-text">
                    <i className="bi bi-hourglass-split me-2"></i>
                    Memuat data...
                  </div>
                )}

                <div className="table-container">
                  <table className="pengiriman-table">
                    <thead>
                      <tr>
                        <th>No</th>
                        <th>No Resi</th>
                        <th>Tanggal</th>
                        <th>Pengirim</th>
                        <th>Penerima</th>
                        <th>Alamat Tujuan</th>
                        <th>Barang</th>
                        <th>Berat</th>
                        <th>Biaya</th>
                        <th>Status</th>
                        <th>Kurir</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pengirimanList.length === 0 ? (
                        <tr>
                          <td colSpan="12" style={{ textAlign: 'center', padding: '40px' }}>
                            {loading ? 'Memuat data...' : 'Tidak ada data pengiriman'}
                          </td>
                        </tr>
                      ) : (
                        pengirimanList.map((item, index) => (
                          <tr key={item.id}>
                            <td>{index + 1}</td>
                            <td><strong>{item.no_resi}</strong></td>
                            <td>{new Date(item.tanggal).toLocaleDateString('id-ID')}</td>
                            <td>
                              <div className="cell-content">
                                <strong>{item.nama_pengirim}</strong>
                                {item.no_telp_pengirim && (
                                  <small>{item.no_telp_pengirim}</small>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="cell-content">
                                <strong>{item.nama_penerima}</strong>
                                {item.no_telp_penerima && (
                                  <small>{item.no_telp_penerima}</small>
                                )}
                              </div>
                            </td>
                            <td>
                              <div>
                                {item.alamat_asal && <div>{item.alamat_asal}</div>}
                              </div>
                            </td>
                            <td>
                              {item.nama_barang.includes('|') ? (
                                // Multiple items
                                <>
                                  {item.nama_barang.split('|').map((nama, idx) => {
                                    const jenisArr = item.jenis_barang ? item.jenis_barang.split(',') : [];
                                    const jenis = jenisArr[idx] ? jenisArr[idx].trim() : '';
                                    const beratPerItem = (parseFloat(item.berat) / item.jumlah_barang).toFixed(2);
                                    
                                    // Ambil nama asli (sudah ada nomornya atau belum)
                                    const trimmedNama = nama.trim();
                                    
                                    return (
                                      <div key={idx}>
                                        <strong>{trimmedNama}</strong>
                                        {jenis && ` (${jenis})`} - {beratPerItem} kg
                                      </div>
                                    );
                                  })}
                                  <div style={{marginTop: '8px', fontWeight: '600'}}>
                                    Total: {item.jumlah_barang} barang, {item.berat} kg
                                  </div>
                                </>
                              ) : (
                                // Single item
                                <>
                                  <strong>{item.nama_barang}</strong>
                                  {item.jenis_barang && <div>{item.jenis_barang}</div>}
                                  <div>
                                    {item.jumlah_barang > 1 && `${item.jumlah_barang}x - `}
                                    {item.berat} kg
                                  </div>
                                </>
                              )}
                            </td>
                            <td>{item.berat} kg</td>
                            <td>{formatCurrency(item.biaya_pengiriman)}</td>
                            <td>{getStatusBadge(item.status)}</td>
                            <td>
                              <div className="cell-content">
                                {item.nama_kurir !== '-' && <strong>{item.nama_kurir}</strong>}
                                {item.no_pol_armada !== '-' && (
                                  <small>{item.no_pol_armada}</small>
                                )}
                                {item.nama_kurir === '-' && item.no_pol_armada === '-' && '-'}
                              </div>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button 
                                  className="btn-print" 
                                  title="Cetak Surat Jalan"
                                  onClick={() => handlePrintSuratJalan(item)}
                                  disabled={loading}
                                >
                                  <i className="bi bi-printer"></i>
                                </button>
                                <button 
                                  className="btn-edit" 
                                  title="Edit"
                                  onClick={() => handleEdit(item.id)}
                                  disabled={loading}
                                >
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                                {user.role === 'admin' && (
                                  <button 
                                    className="btn-delete" 
                                    title="Hapus"
                                    onClick={() => handleDelete(item.id)}
                                    disabled={loading}
                                  >
                                    <i className="bi bi-trash3"></i>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {pengirimanList.length > 0 && (
                  <div className="table-footer">
                    <p>Total: <strong>{pengirimanList.length}</strong> pengiriman</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalMode === 'edit' ? (
                  <>
                    <i className="bi bi-pencil-square me-2"></i>
                    Edit Data Pengiriman
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle me-2"></i>
                    Tambah Data Pengiriman
                  </>
                )}
              </h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleSubmit} className="status-form">
                <h3>
                  <i className="bi bi-file-text me-2"></i>
                  Informasi Dasar
                </h3>
                
                <div className="form-row">
    <div className="form-group">
  <label>No Resi *</label>
  <div className="input-resi-wrapper">
    <input
      type="text"
      name="no_resi"
      value={formData.no_resi}
      onChange={handleChange}
      placeholder="ZT251201123"
      required
      disabled={modalMode === 'edit'}
      className="form-input"
    />
    {modalMode === 'add' && (
      <button 
        type="button" 
        className="btn-generate"
        onClick={generateNoResi}
        title="Generate No Resi Otomatis"
      >
        <i className="bi bi-dice-5"></i>
      </button>
    )}
  </div>
</div>
                  
                  <div className="form-group">
                    <label>Tanggal & Waktu *</label>
                    <input
                      type="datetime-local"
                      name="tanggal"
                      value={formData.tanggal}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Status *</label>
                  <select 
                    name="status" 
                    value={formData.status} 
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="dalamproses">⏳ Dalam Proses</option>
                    <option value="dikirim">🚚 Dikirim</option>
                    <option value="diterima">✅ Diterima</option>
                  </select>
                </div>

                <h3 style={{ marginTop: '20px', marginBottom: '15px', fontSize: '16px' }}>
                  <i className="bi bi-person-up me-2"></i>
                  Data Pengirim
                </h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Nama Pengirim *</label>
                    <input
                      type="text"
                      name="nama_pengirim"
                      value={formData.nama_pengirim}
                      onChange={handleChange}
                      placeholder="Nama lengkap pengirim"
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>No Telp Pengirim</label>
                    <input
                      type="text"
                      name="no_telp_pengirim"
                      value={formData.no_telp_pengirim}
                      onChange={handleChange}
                      placeholder="08xxxxxxxxxx"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Alamat Asal</label>
                  <textarea
                    name="alamat_asal"
                    value={formData.alamat_asal}
                    onChange={handleChange}
                    placeholder="Alamat lengkap pengirim"
                    rows="2"
                    className="form-input"
                  ></textarea>
                </div>

                <h3 style={{ marginTop: '20px', marginBottom: '15px', fontSize: '16px' }}>
                  <i className="bi bi-person-down me-2"></i>
                  Data Penerima
                </h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Nama Penerima *</label>
                    <input
                      type="text"
                      name="nama_penerima"
                      value={formData.nama_penerima}
                      onChange={handleChange}
                      placeholder="Nama lengkap penerima"
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>No Telp Penerima</label>
                    <input
                      type="text"
                      name="no_telp_penerima"
                      value={formData.no_telp_penerima}
                      onChange={handleChange}
                      placeholder="08xxxxxxxxxx"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Alamat Tujuan *</label>
                  <textarea
                    name="alamat_tujuan"
                    value={formData.alamat_tujuan}
                    onChange={handleChange}
                    placeholder="Alamat lengkap tujuan"
                    rows="2"
                    required
                    className="form-input"
                  ></textarea>
                </div>

                <h3 style={{ marginTop: '20px', marginBottom: '15px', fontSize: '16px' }}>
                  <i className="bi bi-box-seam me-2"></i>
                  Detail Barang
                </h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Jumlah Barang</label>
                    <input
                      type="number"
                      name="jumlah_barang"
                      value={formData.jumlah_barang}
                      onChange={handleChange}
                      placeholder="1"
                      min="1"
                      className="form-input"
                    />
                  </div>
<div className="form-group">
  <label>Biaya Pengiriman (Rp) *</label>
  <input
    type="text"
    name="biaya_pengiriman"
    value={formData.biaya_pengiriman ? `Rp ${formatRupiah(formData.biaya_pengiriman)}` : ''}
    onChange={handleBiayaChange}
    placeholder="Rp 0"
    required
    className="form-input"
  />
</div>
                </div>

                {parseInt(formData.jumlah_barang) === 1 ? (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Nama Barang *</label>
                        <input
                          type="text"
                          name="nama_barang"
                          value={formData.nama_barang}
                          onChange={handleChange}
                          placeholder="Nama/deskripsi barang"
                          required
                          className="form-input"
                        />
                      </div>
                      <div className="form-group">
                        <label>Jenis Barang</label>
                        <input
                          type="text"
                          name="jenis_barang"
                          value={formData.jenis_barang}
                          onChange={handleChange}
                          placeholder="Elektronik, Bahan Pokok, Material"
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Berat (kg) *</label>
                        <input
                          type="number"
                          name="berat"
                          value={formData.berat}
                          onChange={handleChange}
                          placeholder="0.0"
                          step="0.1"
                          min="0"
                          required
                          className="form-input"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {detailBarang.map((item, index) => (
                      <div key={index} style={{ 
                        padding: '15px', 
                        background: '#f8f9ff', 
                        borderRadius: '8px', 
                        marginBottom: '15px',
                        border: '2px solid #e8ecff'
                      }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#05339C', fontSize: '14px' }}>
                          <i className="bi bi-box me-2"></i>
                          Barang #{index + 1}
                        </h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Nama Barang *</label>
                            <input
                              type="text"
                              value={item.nama_barang}
                              onChange={(e) => handleDetailBarangChange(index, 'nama_barang', e.target.value)}
                              placeholder="Nama barang"
                              required
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label>Jenis Barang</label>
                            <input
                              type="text"
                              value={item.jenis_barang}
                              onChange={(e) => handleDetailBarangChange(index, 'jenis_barang', e.target.value)}
                              placeholder="Jenis"
                              className="form-input"
                            />
                          </div>
                          <div className="form-group">
                            <label>Berat (kg) *</label>
                            <input
                              type="number"
                              value={item.berat}
                              onChange={(e) => handleDetailBarangChange(index, 'berat', e.target.value)}
                              placeholder="0.0"
                              step="0.1"
                              min="0"
                              required
                              className="form-input"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                <h3 style={{ marginTop: '20px', marginBottom: '15px', fontSize: '16px' }}>
                  <i className="bi bi-truck me-2"></i>
                  Informasi Pengiriman
                </h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>No Pol Armada</label>
                    <input
                      type="text"
                      name="no_pol_armada"
                      value={formData.no_pol_armada}
                      onChange={handleChange}
                      placeholder="L 1234 AB"
                      className="form-input"
                    />
                  </div>
  <div className="form-group">
    <label>Nama Kurir</label>
    <select
      name="nama_kurir"
      value={formData.nama_kurir}
      onChange={handleChange}
      className="form-select"
      style={{ height: '45px', padding: '10px 12px' }}
    >
      <option value="-">Pilih Kurir</option>
      {kurirList.map((kurir) => (
        <option key={kurir.id} value={kurir.nama}>
          {kurir.nama}
        </option>
      ))}
    </select>
  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                    <i className="bi bi-x-circle me-2"></i>
                    Batal
                  </button>
                  <button type="submit" className="btn-submit" disabled={loading}>
                    <i className="bi bi-save me-2"></i>
                    {loading ? 'Menyimpan...' : (modalMode === 'edit' ? 'Update' : 'Simpan')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DataPengiriman;